import { useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { api } from "@/api/client";
import { DEFAULT_LOCALE } from "@/i18n";

type State = "loading" | "authed" | "anon";

/**
 * Gates admin routes on the httpOnly `ce_admin` cookie. The plan (§3) calls for
 * a tiny GET /api/admin/me endpoint to cheaply verify the HMAC cookie — that is
 * a P1 hidden-endpoint item, so until it exists this guard treats ANY non-2xx
 * (incl. the current 404) as unauthenticated and redirects to admin/login.
 * Correct P0 behaviour: the admin SPA isn't usable until P1 wires admin/me +
 * admin/overview anyway.
 */
export default function AdminGuard() {
  const { locale } = useParams();
  const [state, setState] = useState<State>("loading");

  useEffect(() => {
    let alive = true;
    api
      .get("/api/admin/me")
      .then(() => alive && setState("authed"))
      .catch(() => alive && setState("anon"));
    return () => {
      alive = false;
    };
  }, []);

  if (state === "loading") return <div className="p-8 text-txt-tertiary">Loading…</div>;
  if (state === "anon")
    return <Navigate to={`/${locale ?? DEFAULT_LOCALE}/admin/login`} replace />;
  return <Outlet />;
}
